import { all, put, select, takeLatest } from 'redux-saga/effects';
import { getFirebase } from 'react-redux-firebase';
import _get from 'lodash/get';
import ctfClient from 'utils/contentfulService';
import { getIsClientAdmin } from 'containers/Auth/utils';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import {
  GET_ADMIN_USERS,
  GET_VALID_BRANDS,
  UPDATE_USER_ROLE,
} from './constants';
import {
  processing,
  getAdminUsersResult,
  getValidBrandsResult,
  updateAdminUserRoleResult,
} from './actions';

function* getAdminUsers({ payload: { brand } }) {
  try {
    yield put(processing(true));
    const usersDoc = yield getFirebase()
      .firestore()
      .collection('users')
      .where('brand', '==', brand)
      .get();

    const clientDetails = yield select(makeSelectClientDetails());

    const users = usersDoc.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const custom_role_users = users.map(user => ({
      ...user,
      custom_role: getIsClientAdmin(user, clientDetails, brand)
        ? 'Customer Admin'
        : 'User',
      full_name: (_get(user, 'displayName') || '').toLowerCase(),
      isClientAdmin: getIsClientAdmin(user, clientDetails, brand),
    }));

    yield put(getAdminUsersResult(custom_role_users));
  } catch (error) {
    yield put(
      getAdminUsersResult({
        error,
      }),
    );
  } finally {
    yield put(processing(false));
  }
}

function* updateAdminUserRole({ payload }) {
  try {
    yield put(processing(true));

    const { isAdmin, userId, brand } = payload;

    const updateOp = async id => {
      const firestore = getFirebase().firestore();
      const doc = await firestore
        .collection('users')
        .doc(id)
        .get();
      const user = doc.data();

      const updatePayload = {};
      if (isAdmin) {
        if (!user.isClientAdmin) {
          updatePayload.isClientAdmin = true;
        }
        const userClientList = _get(user, 'adminInClients', []);
        userClientList.push(brand);
        updatePayload.adminInClients = userClientList;
      } else {
        const userClientList = _get(user, 'adminInClients', []).filter(
          item => item !== brand,
        );
        updatePayload.adminInClients = userClientList;
        if (user.isClientAdmin && userClientList.length === 0) {
          updatePayload.isClientAdmin = false;
        }
      }

      await firestore
        .collection('users')
        .doc(id)
        .set(updatePayload, {
          merge: true,
        });
    };

    if (Array.isArray(userId)) {
      yield all(
        userId.map(async id => {
          try {
            return await updateOp(id);
          } catch (error) {
            return null;
          }
        }),
      );
    } else {
      yield updateOp(userId);
    }

    yield put(updateAdminUserRoleResult(payload));
  } catch (error) {
    yield put(updateAdminUserRoleResult({ error: true }));
  } finally {
    yield put(processing(false));
  }
}

function* getValidBrands() {
  try {
    yield put(processing(true));

    const response = yield ctfClient.getEntries({
      content_type: 'client',
      select: 'fields.shortName',
      limit: 1000,
    });
    const availableClients = response.items.map(item =>
      _get(item, 'fields.shortName'),
    );

    yield put(getValidBrandsResult(availableClients));
  } catch (error) {
    yield put(getValidBrandsResult([]));
  } finally {
    yield put(processing(false));
  }
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(GET_ADMIN_USERS, getAdminUsers);
  yield takeLatest(UPDATE_USER_ROLE, updateAdminUserRole);
  yield takeLatest(GET_VALID_BRANDS, getValidBrands);
}
